import {
  type BranchDetail,
  BranchDetailSchema,
  type BranchListQuery,
  type BranchListResponse,
  BranchListResponseSchema,
  type CountryOption,
  CountryOptionListSchema,
  type CreateBranchRequest,
  type StateOption,
  StateOptionListSchema,
  type UpdateBranchRequest,
  CityListResponseSchema,
  type CityListItem,
} from '@utazzo/shared';

import { apiClient } from '@/lib/api-client';

/**
 * Branch master + geography lookups API surface. Every response is validated
 * with the shared Zod schema so a server change can't sneak past the UI types.
 */

export const listBranches = async (query: BranchListQuery): Promise<BranchListResponse> => {
  const { data } = await apiClient.get<unknown>('/branches', { params: query });
  return BranchListResponseSchema.parse(data);
};

export const getBranch = async (id: string): Promise<BranchDetail> => {
  const { data } = await apiClient.get<unknown>(`/branches/${id}`);
  return BranchDetailSchema.parse(data);
};

export const createBranch = async (payload: CreateBranchRequest): Promise<BranchDetail> => {
  const { data } = await apiClient.post<unknown>('/branches', payload);
  return BranchDetailSchema.parse(data);
};

export const updateBranch = async (
  id: string,
  payload: UpdateBranchRequest,
): Promise<BranchDetail> => {
  const { data } = await apiClient.patch<unknown>(`/branches/${id}`, payload);
  return BranchDetailSchema.parse(data);
};

export const deleteBranch = async (id: string): Promise<void> => {
  await apiClient.delete(`/branches/${id}`);
};

export const bulkDeleteBranches = async (
  ids: string[],
): Promise<{ updated: number; failed: { id: string; error: string }[] }> => {
  const { data } = await apiClient.post<{
    updated: number;
    failed: { id: string; error: string }[];
  }>('/branches/bulk-delete', { ids });
  return data;
};

export const bulkSetBranchesStatus = async (
  ids: string[],
  isActive: boolean,
): Promise<{ updated: number; failed: { id: string; error: string }[] }> => {
  const { data } = await apiClient.post<{
    updated: number;
    failed: { id: string; error: string }[];
  }>('/branches/bulk-set-status', { ids, isActive });
  return data;
};

// ----- Geography lookups (shared with cities feature) -----

export const listCountryOptions = async (): Promise<CountryOption[]> => {
  const { data } = await apiClient.get<unknown>('/countries/options');
  return CountryOptionListSchema.parse(data);
};

export const listStateOptions = async (countryId: string): Promise<StateOption[]> => {
  const { data } = await apiClient.get<unknown>('/states/options', { params: { countryId } });
  return StateOptionListSchema.parse(data);
};

export const listCityOptions = async (
  countryId: string,
  stateId?: string,
): Promise<CityListItem[]> => {
  const { data } = await apiClient.get<unknown>('/cities', {
    params: {
      countryId,
      ...(stateId ? { stateId } : {}),
      pageSize: 200,
      sortBy: 'name',
      sortDir: 'asc',
    },
  });
  return CityListResponseSchema.parse(data).data;
};
